import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { Character } from "../character";
import { planReversal } from "../character";
import type { LinkResolver } from "../data/links";
import type { RollRecord } from "../rolls";
import type { LogEntry, ReversalPlan, RollLog as RollLogModel } from "../session";
import { ROLL_LOG_CAP, filterEntries, groupByRound, isLive } from "../session";
import type { ActorContext } from "./enrich";
import { Modal } from "./modal";
import { CheckBreakdown, CheckDice, DamageResultView, DegreeBadge, Notes } from "./roll-panel";
import { useSheetActions } from "./sheet-context";

type Filter = "all" | "roll" | "session";
const FILTERS: { id: Filter; label: string }[] = [{ id: "all", label: "All" }, { id: "roll", label: "Rolls" }, { id: "session", label: "Session" }];
/** Entries rendered at once; more come as the list is scrolled (or on the button). */
const PAGE = 100;

interface Props {
  character: Character;
  busy: boolean;
  actor: ActorContext | null;
  links: LinkResolver | null;
  /** Reverse an entry (and its dependents, already confirmed here). */
  onReverse: (id: string) => Promise<void>;
}

/**
 * The Roll Log tab (issue #20): every Play Action of the Session newest first, grouped by round
 * while rounds are in use, filtered to Rolls or Session changes; a tap opens the full breakdown;
 * Reverse applies the inverse, after a confirmation naming the later entries that depend on it.
 * Rows render in pages of 100 with `content-visibility: auto`, so 500 entries scroll without jank.
 */
export function RollLog({ character, busy, actor, links, onReverse }: Props) {
  const log = character.rollLog;
  const session = character.session;
  const [filter, setFilter] = useState<Filter>("all");
  const [open, setOpen] = useState<string | null>(null);
  const [limit, setLimit] = useState(PAGE);
  const [confirm, setConfirm] = useState<ReversalPlan | null>(null);
  const sentinel = useRef<HTMLLIElement>(null);
  const shown = useMemo(() => filterEntries(log, filter), [log, filter]);
  const page = shown.slice(0, limit);
  const roundsInUse = session?.round !== null || log.entries.some((e) => e.round !== null);
  const groups = roundsInUse ? groupByRound(page) : [{ round: null, entries: page }];
  const more = shown.length > limit;

  useEffect(() => {
    if (!more || !sentinel.current || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((hits) => {
      if (hits.some((h) => h.isIntersecting)) setLimit((n) => n + PAGE);
    });
    observer.observe(sentinel.current);
    return () => observer.disconnect();
  }, [more, limit]);

  if (!session) {
    return (
      <section class="roll-log">
        <h3>Roll Log</h3>
        <p class="muted">No Session is open. The Roll Log records a Session's Play Actions, Rolls included, and is kept with it.</p>
      </section>
    );
  }
  const reverse = (entry: LogEntry) => {
    const plan = planReversal(character, entry.id);
    if (!plan) return;
    if (plan.dependents.length === 0) void onReverse(entry.id);
    else setConfirm(plan);
  };
  const confirmed = async () => {
    const plan = confirm;
    setConfirm(null);
    if (plan) await onReverse(plan.entry.id);
  };

  return (
    <section class="roll-log">
      <div class="row">
        <h3>Roll Log</h3>
        <span class="actions">
          <span class="segmented" aria-label="Filter">
            {FILTERS.map((f) => <button type="button" key={f.id} class={filter === f.id ? "active" : ""} onClick={() => { setFilter(f.id); setLimit(PAGE); }}>{f.label}</button>)}
          </span>
          <span class="muted">{shown.length} {shown.length === 1 ? "entry" : "entries"}</span>
        </span>
      </div>
      {shown.length === 0 ? (
        <p class="muted">{log.entries.length === 0 ? "Nothing yet. Every Roll and Session change lands here as it happens." : "Nothing matches the filter."}</p>
      ) : (
        <ul class="plain log-entries">
          {groups.map((g, gi) => (
            <li key={`${g.round ?? "none"}-${gi}`} class="log-group">
              {roundsInUse && <h4 class="log-round">{g.round === null ? "Outside encounters" : `Round ${g.round}`}</h4>}
              <ul class="plain">
                {g.entries.map((entry) => (
                  <LogRow key={entry.id} entry={entry} log={log} open={open === entry.id} busy={busy} actor={actor} links={links}
                    onToggle={() => setOpen(open === entry.id ? null : entry.id)} onReverse={() => reverse(entry)} onJump={(id) => { setOpen(id); }} />
                ))}
              </ul>
            </li>
          ))}
          {more && (
            <li ref={sentinel} class="log-more">
              <button type="button" onClick={() => setLimit((n) => n + PAGE)}>Show older ({shown.length - limit} more)</button>
            </li>
          )}
          {log.dropped > 0 && !more && <li class="muted log-dropped">{log.dropped} older {log.dropped === 1 ? "entry was" : "entries were"} dropped: the Roll Log keeps the last {ROLL_LOG_CAP}.</li>}
        </ul>
      )}
      {confirm && (
        <Modal title="Reverse with what depends on it" onClose={() => setConfirm(null)}>
          <p>Later entries changed the same things. Reversing <strong>{confirm.entry.summary}</strong> also reverses, newest first:</p>
          <ul class="plain log-dependents">
            {[...confirm.dependents].reverse().map((d) => <li key={d.id}><span class="muted">{time(d.at)}</span> {d.summary}</li>)}
          </ul>
          <div class="actions">
            <button type="button" class="danger" disabled={busy} onClick={() => void confirmed()}>Reverse {confirm.dependents.length + 1} entries</button>
            <button type="button" disabled={busy} onClick={() => setConfirm(null)}>Cancel</button>
          </div>
        </Modal>
      )}
    </section>
  );
}

interface RowProps {
  entry: LogEntry;
  log: RollLogModel;
  open: boolean;
  busy: boolean;
  actor: ActorContext | null;
  links: LinkResolver | null;
  onToggle: () => void;
  onReverse: () => void;
  onJump: (id: string) => void;
}

function LogRow({ entry, log, open, busy, actor, links, onToggle, onReverse, onJump }: RowProps) {
  const reversed = entry.reversedBy !== null;
  const roll = entry.action?.kind === "roll" ? entry.action.roll : null;
  const linked = entry.reverses ? log.entries.find((e) => e.id === entry.reverses) : null;
  const reverser = entry.reversedBy ? log.entries.find((e) => e.id === entry.reversedBy) : null;
  return (
    <li class={`log-entry${reversed ? " reversed" : ""}${open ? " open" : ""}${entry.type === "note" ? " note" : ""}`} data-id={entry.id}>
      <button type="button" class="log-summary" aria-expanded={open} onClick={onToggle}>
        <span class="log-time muted">{time(entry.at)}</span>
        <span class={`tag log-kind kind-${entry.type}`}>{kindLabel(entry)}</span>
        <span class="log-text">{entry.summary}</span>
        {reversed && <span class="tag warn">reversed</span>}
      </button>
      {open && (
        <div class="log-detail">
          {roll && <RollDetail record={roll} actor={actor} links={links} />}
          {entry.events.length > 0 && (
            <ul class="plain events">
              {entry.events.map((e, i) => <li key={i} class={e.kind === "blocked" ? "notice" : e.kind === "dead" || e.kind === "massive-damage" ? "warn" : ""}><span class="tag">{e.kind === "roll-needed" ? "roll" : e.kind}</span> {e.text}</li>)}
            </ul>
          )}
          {roll?.rerollOf && <p class="muted">Rerolled the Roll made at {rollTime(log, roll.rerollOf) ?? "an earlier time"}; only this result counts.</p>}
          {entry.reverses && (
            <p class="muted">Reverses the entry {linked ? <button type="button" class="linkish" onClick={() => onJump(linked.id)}>{time(linked.at)}: {linked.summary}</button> : "no longer kept"}.</p>
          )}
          {reversed && (
            <p class="muted">Reversed {reverser ? <button type="button" class="linkish" onClick={() => onJump(reverser.id)}>at {time(reverser.at)}</button> : "by an entry no longer kept"}.</p>
          )}
          <div class="actions">
            {isLive(entry) && <button type="button" disabled={busy} onClick={onReverse}>Reverse</button>}
            {entry.type === "note" && <span class="muted">A note; nothing to reverse.</span>}
          </div>
        </div>
      )}
    </li>
  );
}

/** A Roll's full breakdown, as the Roll panel showed it. */
function RollDetail({ record, actor, links }: { record: RollRecord; actor: ActorContext | null; links: LinkResolver | null }) {
  const { roll } = useSheetActions();
  switch (record.kind) {
    case "check":
      return (
        <div class="log-roll">
          <CheckDice result={record.result} />
          <p class="inline">
            {record.result.degree ? <><span class="muted">DC {record.result.degree.dc}</span> <DegreeBadge result={record.result} /></> : <span class="muted">No DC entered.</span>}
            {record.choices.map > 0 && <span class="tag">{record.choices.map === 1 ? "2nd attack" : "3rd attack"}</span>}
            {record.choices.rangeIncrement !== null && record.choices.rangeIncrement > 1 && <span class="tag">range increment {record.choices.rangeIncrement}</span>}
            {record.choices.volley && <span class="tag">volley</span>}
          </p>
          <CheckBreakdown spec={record.spec} result={record.result} />
          {record.result.notes.length > 0 && <Notes notes={record.result.notes} actor={actor} links={links} onFollow={(request) => roll(request)} />}
          {record.spec.traits.length > 0 && <p class="roll-traits">{record.spec.traits.map((t) => <span key={t} class="tag trait">{t}</span>)}</p>}
        </div>
      );
    case "damage":
      return (
        <div class="log-roll">
          <DamageResultView result={record.result} />
          <section class="breakdown" aria-label="Dice">
            <h3>Dice</h3>
            <ul class="plain breakdown-rows">
              <li><span class="bd-label">{record.critical && record.criticalFormula ? record.criticalFormula : record.formula}{record.critical && <span class="tag">critical</span>}</span></li>
              {record.result.instances.map((inst, i) => (
                <li key={i}>
                  <span class="bd-label">{inst.persistent ? "persistent " : ""}{inst.type === "untyped" ? (inst.kind === "healing" ? "healing" : "damage") : inst.type}</span>
                  <span class="bd-source muted">{inst.dice.map((d) => `${d.results.length}d${d.faces}: ${d.results.join(", ")}`).join(" · ") || "no dice"}</span>
                  <span class="bd-value">{inst.total}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>
      );
    case "inline":
      return (
        <div class="log-roll">
          <div class="roll-dice">
            <span class="roll-sum"><span class="muted">{record.formula} =</span> <span class="roll-total">{record.result.total}</span></span>
            {record.flavor && <span class="tag">{record.flavor}</span>}
          </div>
          {record.result.dice.length > 0 && <p class="muted">{record.result.dice.map((d) => `${d.results.length}d${d.faces}: ${d.results.join(", ")}`).join(" · ")}</p>}
        </div>
      );
  }
}

function kindLabel(entry: LogEntry): string {
  if (entry.type === "note") return "note";
  if (entry.kind === "reversal") return "reversal";
  if (entry.type === "roll") return "roll";
  return entry.kind.replace(/-/g, " ");
}

function time(at: string): string {
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? at : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function rollTime(log: RollLogModel, rollId: string): string | null {
  const entry = log.entries.find((e) => e.action?.kind === "roll" && e.action.roll.id === rollId);
  return entry ? time(entry.at) : null;
}
